<template>
  <div
    class="w-full rounded-lg flex flex-col gap-2 p-1 max-w-full overflow-auto"
  >
    <div
      class="flex flex-col gap-2 overflow-y-auto scrollbar-thin scrollbar-tema flex-1 min-h-0"
    >
      <!-- Movimentos Fechados Table -->
      <div
        class="w-full panel flex-shrink-0"
        style="background-color: var(--fundoForm)"
      >
        <GenericDataTable
          :key="`movimentos-table-${armazemId}`"
          ref="movimentosTableRef"
          @row-click="null"
          @action="handleTableAction"
          :api-url="null"
          :useServerSide="false"
          :columns="movimentosColumns"
          :tableData="movimentos"
          :show_export="true"
          :show_close_btn="true"
          @close="$emit('close')"
          table_title="Movimentos Fechados para Aprovação"
        />
      </div>

      <!-- Items Table -->
      <div
        v-if="movimentoSelecionado && items.length > 0"
        class="w-full panel flex-shrink-0 bg-['var(--fundoForm)'] flex gap-2"
      >
        <div class="w-full">
          <GenericDataTable
            :key="`items-table-${movimentoSelecionado?.id}`"
            ref="itemsTableRef"
            @row-click="null"
            :api-url="null"
            :useServerSide="false"
            :columns="itemsColumns"
            :tableData="items"
            :show_export="true"
            :show_close_btn="false"
            table_title=""
          >
            <template #left-actions>
              <button
                v-if="movimentoSelecionado && !movimentoSelecionado.aprovado && validPermission('SM0004')"
                @click="askConfirmationToApprove"
                class="action-btn aprovar-btn"
                title="Aprovar Movimento"
              >
                <i class="fa-solid fa-thumbs-up"></i> Aprovar Movimento
              </button>
              <div
                v-if="movimentoSelecionado && movimentoSelecionado.aprovado"
                class="flex justify-center items-center p-4"
              >
                <span class="text-green-400 font-semibold">
                  <i class="fa-solid fa-check-circle"></i> Este movimento já foi
                  aprovado
                </span>
              </div>
            </template>
          </GenericDataTable>
        </div>
      </div>
      <!-- Movimento Details and Items -->
      <div
        v-if="movimentoSelecionado"
        class="w-full panel flex-shrink-0"
        style="background-color: var(--fundoForm)"
      >
        <div class="panel-header-collapsible">
          <div class="flex items-center justify-between gap-2">
            <h3 class="panel-header mb-0">
              Movimento #{{ movimentoSelecionado.id }}
            </h3>
            <div class="flex items-center gap-2">
              <button
                class="action-btn"
                @click="toggleDetails"
                title="Mostrar/Ocultar detalhes"
              >
                <i
                  :class="
                    detailsVisible
                      ? 'fa-solid fa-chevron-up'
                      : 'fa-solid fa-chevron-down'
                  "
                ></i>
                <span class="ml-2">{{
                  detailsVisible ? "Ocultar" : "Detalhes"
                }}</span>
              </button>
            </div>
          </div>
        </div>

        <div v-if="detailsVisible" class="panel-content">
          <div class="info-grid-horizontal">
            <div class="info-section">
              <h4 class="info-section-title">Dados do Movimento</h4>
              <div class="info-row">
                <span class="info-label">Data Criado:</span>
                <span class="info-value">{{
                  movimentoSelecionado.data_criado?.split("T")[0]
                }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Criado por:</span>
                <span class="info-value">{{
                  movimentoSelecionado.criado_por
                }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Fechado em:</span>
                <span class="info-value">{{
                  movimentoSelecionado.data_fechado?.split("T")[0]
                }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Status:</span>
                <span
                  class="info-value font-semibold"
                  :class="getStatusClass(movimentoSelecionado.status)"
                >
                  {{ movimentoSelecionado.status }}
                </span>
              </div>
            </div>

            <div class="info-section">
              <h4 class="info-section-title">Origem e Destino</h4>
              <div class="info-row">
                <span class="info-label">Armazém Origem:</span>
                <span class="info-value">{{
                  movimentoSelecionado.armazem_origem_nome
                }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Armazém Destino:</span>
                <span class="info-value">{{
                  movimentoSelecionado.armazem_destino_nome
                }}</span>
              </div>
            </div>

            <div class="info-section">
              <h4 class="info-section-title">Descrição</h4>
              <div class="info-row">
                <span class="info-value">{{
                  movimentoSelecionado.descricao || "Sem descrição"
                }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirm Dialog -->
    <ConfirmComponent
      v-if="showConfirmDialog"
      :message="confirmMessage"
      @confirm="handleConfirmAction"
      @cancel="cancelConfirmAction"
    />
  </div>
</template>

<script>
import axios from "axios";
import GenericDataTable from "@/components/dataTable/GenericDataTable2.vue";
import { toast } from "vue3-toastify";
import ConfirmComponent from "@/components/simpleConfirmComponent.vue";
import permissionMixin from "@/permissionMixin.js";

export default {
  name: "MovimentosFechados",
  mixins: [permissionMixin],
  components: {
    GenericDataTable,
    ConfirmComponent,
  },
  props: {
    armazemId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      baseURL: process.env.VUE_APP_URL_BASE,
      movimentos: [],
      items: [],
      movimentoSelecionado: null,
      detailsVisible: false,
      showConfirmDialog: false,
      confirmMessage: "",
      loading: false,
    };
  },
  computed: {
    movimentosColumns() {
      return [
        {
          key: "id",
          label: "ID",
          sortable: true,
          type: "number",
        },

        {
          key: "armazem_origem_nome",
          label: "Origem",
          sortable: false,
          type: "string",
        },
        {
          key: "data_fechado",
          label: "Data Fechado",
          sortable: true,
          type: "date",
          formatter: (value) => value?.split("T")[0],
        },
        // {
        //   key: "criado_por",
        //   label: "Criado Por",
        //   sortable: false,
        //   type: "string",
        // },
        {
          key: "fechado_por",
          label: "Fechado Por",
          sortable: false,
          type: "string",
        },

        // {
        //   key: "aprovado",
        //   label: "Aprovado",
        //   sortable: true,
        //   type: "boolean",
        // },
        {
          key: "",
          label: "Ações",
          sortable: false,
          type: "actions",
          maxWidth: 100,
          actions: [
            {
              type: "view",
              icon: "fa-solid fa-eye",
              class:
                "action-icon-btn text-blue-500 bg-blue-100/20 hover:bg-blue-100",
              title: "Ver Detalhes",
            },
          ],
        },
      ];
    },
    itemsColumns() {
      return [
        { key: "equipamento.nome", label: "Equipamento", maxWidth: 200 },
        {
          key: "quantidade",
          label: "Quantidade",
          type: "number",
          maxWidth: 120,
        },
        // {
        //   key: "status",
        //   label: "Status",
        //   type: "badge",
        //   maxWidth: 100,
        // },
      ];
    },
  },
  methods: {
    async fetchMovimentos() {
      this.loading = true;
      try {
        const res = await axios.get(
          `${this.baseURL}/api/v1/movimentoestoque/fechados/?armazem_id=${this.armazemId}&role=destino`,
        );
        this.movimentos = res.data.data || [];
        if (this.movimentos.length === 0) {
          toast.info("Nenhum movimento fechado encontrado para este armazém.");
        }
      } catch (error) {
        console.error("Error fetching movimentos:", error);
        toast.error("Erro ao buscar movimentos fechados.");
        this.movimentos = [];
      } finally {
        this.loading = false;
      }
    },
    async fetchItems(movimentoId) {
      try {
        const res = await axios.get(
          `${this.baseURL}/api/v1/itemmovimentoestoque/?movimento_estoque_id=${movimentoId}`,
        );
        this.items = res.data.data || [];
      } catch (error) {
        console.error("Error fetching items:", error);
        toast.error("Erro ao buscar itens do movimento.");
        this.items = [];
      }
    },
    async handleTableAction({ type, item }) {
      if (type === "view") {
        this.movimentoSelecionado = item;
        this.detailsVisible = false;
        // fetch items immediately when user clicks view
        await this.fetchItems(item.id);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    },
    async toggleDetails() {
      this.detailsVisible = !this.detailsVisible;
      if (
        this.detailsVisible &&
        this.movimentoSelecionado &&
        (!this.items || this.items.length === 0)
      ) {
        await this.fetchItems(this.movimentoSelecionado.id);
      }
    },
    askConfirmationToApprove() {
      this.showConfirmDialog = true;
      this.confirmMessage = `Tem certeza que deseja aprovar este movimento de estoque (#${this.movimentoSelecionado.id})?`;
    },
    async handleConfirmAction() {
      this.showConfirmDialog = false;
      await this.approveMovimento();
    },
    cancelConfirmAction() {
      this.showConfirmDialog = false;
      this.confirmMessage = "";
    },
    async approveMovimento() {
      try {
        const payload = { aprovar: true };
        const res = await axios.post(
          `${this.baseURL}/api/v1/movimentoestoque/${this.movimentoSelecionado.id}/close/`,
          payload,
        );

        if (res.data) {
          toast.success("Movimento aprovado com sucesso!");
          this.movimentoSelecionado.aprovado = true;
          this.movimentoSelecionado.status = "aprovado";

          // Refresh the movements list
          await this.fetchMovimentos();
          this.clearSelection();
        }
      } catch (error) {
        console.error("Error approving movimento:", error);
        toast.error(
          `Erro ao aprovar movimento: ${
            error.response?.data?.message || error.message
          }`,
        );
      }
    },
    clearSelection() {
      this.movimentoSelecionado = null;
      this.items = [];
    },
    getStatusClass(status) {
      const statusMap = {
        fechado: "status-pending",
        aprovado: "status-active",
        desativado: "status-inactive",
      };
      return statusMap[status?.toLowerCase()] || "";
    },
    goBack() {
      this.$router.push({ name: "stocks-backoffice", query: { tab: "movimento" } });
    },
  },
  mounted() {
    this.fetchMovimentos();
  },
};
</script>

<style scoped>
/* Panel structure */
.panel {
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.panel-header-collapsible {
  padding: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.panel-header {
  font-size: 1.125rem;
  font-weight: 600;
  color: white;
  margin: 0;
}

.panel-content {
  padding: 1rem;
}

/* Info grid */
.info-grid-horizontal {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

@media (max-width: 1200px) {
  .info-grid-horizontal {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .info-grid-horizontal {
    grid-template-columns: 1fr;
  }
}

.info-section {
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  gap: 0.5rem;
  padding: 1rem;
}

.info-section-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Info rows */
.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.4rem 0.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.875rem;
}

.info-value {
  color: white;
  font-size: 0.875rem;
  text-align: right;
}

/* Action buttons */
.action-btn {
  padding: 0.3rem 1rem;
  background-color: var(--button);
  color: white;
  border-radius: 0.375rem;
  transition: opacity 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  border: none;
  cursor: pointer;
}

.aprovar-btn {
  background-color: #10b981; /* Green */
}

.action-btn:hover {
  opacity: 0.8;
}

/* Status badges */
.status-active {
  color: #10b981;
  font-weight: 600;
}

.status-inactive {
  color: #ef4444;
  font-weight: 600;
}

.status-pending {
  color: #f59e0b;
  font-weight: 600;
}

/* Utility classes */
.mb-0 {
  margin-bottom: 0;
}

.font-semibold {
  font-weight: 600;
}
</style>
